<script setup lang="ts">
/**
 * JSON editing field: a monospace text area that parses as you type (after a
 * short pause) and says where the JSON breaks — „Zeile 3, Spalte 12“ — plus a
 * „Formatieren“ action. `v-model` is the parsed value; it only changes while
 * the text is valid and `validate` accepts it. `v-model:text` is the raw text,
 * for callers that keep the text as typed. Tab inserts two spaces
 * (Shift+Tab and Escape then Tab leave the field as usual).
 *
 * The field grows with its content from `rows` up to `maxRows`.
 */
import { computed, onBeforeUnmount, ref, useId, watch } from 'vue';
import { parseJson, stringifyJson } from './parseJson';

const props = withDefaults(
  defineProps<{
    label?: string;
    hint?: string;
    rows?: number;
    maxRows?: number;
    disabled?: boolean;
    readonly?: boolean;
    placeholder?: string;
    /** Extra check on the parsed value: an error text, or null/undefined when fine. */
    validate?: (value: unknown) => string | null | undefined;
    /** Pause after typing before parsing, in ms. */
    debounce?: number;
    formatLabel?: string;
    /** Position text; `{line}` and `{column}` are replaced. */
    positionLabel?: string;
  }>(),
  {
    label: undefined,
    hint: undefined,
    rows: 6,
    maxRows: 24,
    disabled: false,
    readonly: false,
    placeholder: undefined,
    validate: undefined,
    debounce: 250,
    formatLabel: 'Formatieren',
    positionLabel: 'Zeile {line}, Spalte {column}',
  }
);

/** The parsed value. */
const model = defineModel<unknown>();
/** The raw text. */
const textModel = defineModel<string>('text');

const emit = defineEmits<{
  /** Whether the current text is valid JSON that `validate` accepts. */
  validity: [valid: boolean];
}>();

const id = useId();
const errorId = `${id}-error`;
const hintId = `${id}-hint`;

const text = ref(textModel.value ?? stringifyJson(model.value) ?? '');
const error = ref<{ message: string; where?: string } | null>(null);
/** The value this field last emitted — echoes of it must not rewrite the text. */
let lastEmitted: unknown = model.value;
let timer: ReturnType<typeof setTimeout> | undefined;

function check(now = text.value) {
  clearTimeout(timer);
  if (!now.trim()) {
    error.value = null;
    emit('validity', true);
    lastEmitted = undefined;
    model.value = undefined;
    return;
  }
  const res = parseJson(now);
  if (!res.ok) {
    error.value = {
      message: res.message,
      where:
        res.line === undefined
          ? undefined
          : props.positionLabel
              .replace('{line}', String(res.line))
              .replace('{column}', String(res.column)),
    };
    emit('validity', false);
    return;
  }
  const problem = props.validate?.(res.value);
  if (problem) {
    error.value = { message: problem };
    emit('validity', false);
    return;
  }
  error.value = null;
  emit('validity', true);
  lastEmitted = res.value;
  model.value = res.value;
}

function onInput(event: Event) {
  text.value = (event.target as HTMLTextAreaElement).value;
  textModel.value = text.value;
  clearTimeout(timer);
  timer = setTimeout(() => check(), props.debounce);
}

function format() {
  const res = parseJson(text.value);
  if (!res.ok) {
    check();
    return;
  }
  const pretty = stringifyJson(res.value);
  if (pretty === null) return;
  text.value = pretty;
  textModel.value = pretty;
  check(pretty);
}

/** After Escape the next Tab leaves the field instead of indenting (no keyboard trap). */
let tabLeaves = false;

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    tabLeaves = true;
    return;
  }
  if (event.key !== 'Tab') {
    tabLeaves = false;
    return;
  }
  if (event.shiftKey || props.readonly || tabLeaves) {
    tabLeaves = false;
    return;
  }
  event.preventDefault();
  const el = event.target as HTMLTextAreaElement;
  const { selectionStart: a, selectionEnd: b } = el;
  // execCommand keeps the browser's undo stack; fall back to a plain splice.
  if (!document.execCommand?.('insertText', false, '  ')) {
    el.value = `${el.value.slice(0, a)}  ${el.value.slice(b)}`;
    el.selectionStart = el.selectionEnd = a + 2;
  }
  onInput(event);
}

// Outside changes: a new value (not our own echo) or new text replace the field.
watch(model, (value) => {
  if (value === lastEmitted) return;
  lastEmitted = value;
  text.value = stringifyJson(value) ?? '';
  error.value = null;
});
watch(textModel, (value) => {
  if (value === undefined || value === text.value) return;
  text.value = value;
  check(value);
});

// Text handed in from outside is checked right away: a field that starts
// broken shows its error without waiting for the first keystroke.
if (textModel.value?.trim()) check(textModel.value);

onBeforeUnmount(() => clearTimeout(timer));

const lineCount = computed(() => text.value.split('\n').length);
const visibleRows = computed(() => Math.max(props.rows, Math.min(props.maxRows, lineCount.value)));
const describedBy = computed(
  () =>
    [error.value ? errorId : null, props.hint ? hintId : null].filter(Boolean).join(' ') ||
    undefined
);
</script>

<template>
  <div class="nx-json-input" :class="{ 'is-invalid': !!error, 'is-disabled': props.disabled }">
    <div v-if="props.label || !props.readonly" class="nx-json-input__head">
      <label v-if="props.label" class="nx-json-input__label" :for="id">{{ props.label }}</label>
      <button
        v-if="!props.readonly"
        type="button"
        class="nx-json-input__format"
        :disabled="props.disabled"
        data-testid="nx-json-format"
        @click="format"
      >
        {{ props.formatLabel }}
      </button>
    </div>
    <textarea
      :id="id"
      class="nx-json-input__area"
      :value="text"
      :rows="visibleRows"
      :disabled="props.disabled"
      :readonly="props.readonly"
      :placeholder="props.placeholder"
      spellcheck="false"
      autocomplete="off"
      autocapitalize="off"
      :aria-invalid="!!error || undefined"
      :aria-describedby="describedBy"
      data-testid="nx-json-area"
      @input="onInput"
      @keydown="onKeydown"
      @blur="
        tabLeaves = false;
        check();
      "
    />
    <p v-if="error" :id="errorId" class="nx-json-input__error" role="alert">
      <strong v-if="error.where">{{ error.where }}:</strong>
      {{ error.message }}
    </p>
    <p v-if="props.hint" :id="hintId" class="nx-json-input__hint">{{ props.hint }}</p>
  </div>
</template>

<style scoped>
.nx-json-input {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
  min-width: 0;
}
.nx-json-input__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--nxt-space-sm);
}
.nx-json-input__label {
  color: var(--nxt-label-fg, var(--nxt-fg-subtle));
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-json-input__format {
  margin-inline-start: auto;
  padding: 0;
  border: none;
  background: none;
  color: var(--nxt-brand-subtle-fg);
  font: inherit;
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
  cursor: pointer;
}
.nx-json-input__format:hover {
  text-decoration: underline;
}
.nx-json-input__format:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.nx-json-input__format:focus-visible {
  border-radius: var(--nxt-radius-sm);
  outline: none;
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-json-input__area {
  width: 100%;
  min-width: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
  color: var(--nxt-fg);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
  tab-size: 2;
  resize: vertical;
  scrollbar-width: thin;
  scrollbar-color: var(--nxt-border-strong) transparent;
}
.nx-json-input__area:focus-visible {
  outline: none;
  border-color: var(--nxt-brand-border);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-json-input.is-invalid .nx-json-input__area {
  border-color: var(--nxt-danger-border);
}
.nx-json-input.is-disabled .nx-json-input__area {
  opacity: 0.6;
}
.nx-json-input__error {
  margin: 0;
  color: var(--nxt-danger-subtle-fg);
  font-size: var(--nxt-font-size-xs);
  overflow-wrap: anywhere;
}
.nx-json-input__hint {
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
</style>
